import { TrpcClient } from 'src/appState/TrpcClient';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { computed } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import {
    BlockingNotificationType,
    NonBlockingNotificationType,
} from 'src_server/trpc/types/accountModel/accountResponse';
import { Common } from 'src/domains/common/Common';

export class BlockingNotificationsState {
    private readonly basicDataComp: BasicDataModel;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly common: Common
    ) {
        this.basicDataComp = BasicDataModel.get(this.common);
    }

    @computed public get getBlockingNotificationType(): BlockingNotificationType | NonBlockingNotificationType | null {
        const customerData = this.basicDataComp.basicDataReady;
        if (customerData === null) {
            return null;
        }

        const allNotifications = customerData.prompts?.notificationList ?? [];
        const activeNotifications = [...allNotifications].filter((notification) => notification.active);

        for (const notification of activeNotifications) {
            switch (notification.name) {
                case 'changeOfLicense':
                    return BlockingNotificationType.CHANGE_OF_LICENSE;
                case 'privacyUpdate':
                    return BlockingNotificationType.PRIVACY_UPDATE;
                case 'termsUpdate':
                    return BlockingNotificationType.TERMS_UPDATE;
                case 'contactPreferencesUpdate':
                    return BlockingNotificationType.CONTACT_PREFERENCES;
                case 'ringfencedFundsUpdate':
                    return BlockingNotificationType.RINGFENCED_FUNDS_UPDATE;
                case 'rtpUpdate':
                    return BlockingNotificationType.RTP_UPDATE;
                case 'kycDepositRestriction':
                    if (
                        (customerData.depositRestriction?.source === 'kycThreshold' ||
                            customerData.depositRestriction?.source === 'backoffice') &&
                        this.checkIsDepositRestrictionActive()
                    ) {
                        return BlockingNotificationType.KYC_DEPOSIT_RESTRICTION;
                    }
                    break;
                default:
                    break;
            }
        }

        return null;
    }

    private checkIsDepositRestrictionActive = (): boolean => {
        const depositRestriction = this.basicDataComp.basicDataReady?.depositRestriction ?? null;

        if (depositRestriction === null) {
            return false;
        }

        if (depositRestriction.permanent === true) {
            return true;
        }

        if (DateTime.current().isBefore(depositRestriction.expiryDate ?? '')) {
            return true;
        }

        return false;
    };

    public handleConfirmNewLicenceNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.CHANGE_OF_LICENSE);
    };

    public handleConfirmContactPreferences = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.CONTACT_PREFERENCES);
    };
    public handleConfirmRingfencedfunds = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.RINGFENCED_FUNDS_UPDATE);
    };
    public handleConfirmRTPNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.RTP_UPDATE);
    };

    public handleConfirmNewPrivacyPolicy = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.PRIVACY_UPDATE);
    };

    public handleConfirmNewTerms = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.TERMS_UPDATE);
    };

    public handleConfirmKycDepositRestrictionNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.KYC_DEPOSIT_RESTRICTION);
    };

    private onConfirm = async (blockingNotificationType: BlockingNotificationType): Promise<void> => {
        const confirmedNotification = { name: blockingNotificationType, active: false };

        const allNotifications = this.basicDataComp.basicDataReady?.prompts?.notificationList ?? [];
        const updatedNnotifications = allNotifications.map((notification) =>
            notification.name === blockingNotificationType ? confirmedNotification : notification
        );

        await this.trpcClient.client.accounts.approveNotification.mutate({
            prompts: { notificationList: updatedNnotifications },
        });

        this.basicDataComp.basicData.refresh();
    };
}
